





































































































































import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { getEditedOrderMessage } from '@/models/order-history/supersede-status'
import { getPaymentLabel } from '@/models/purhase/payment-type'
import { formatPoint } from '@/util/format'
import MyColorConfig from '@ajax/modules/config'
import CopyButton from '@spa/components/molecules/CopyButton.vue'
import { AddToCartButtonEligibilityChecker } from '@spa/components/organisms/mypage/order/model/add-to-cart-button'
import { getOrderHistoryDeliveryMethodLabel } from '@spa/components/organisms/mypage/order/model/delivery-method-label'
import { OrderStatusViewModel } from '@spa/components/organisms/mypage/order/model/order-status-view'
import {
  ReorderAcceptabilityState,
  reorderIsAcceptable,
} from '@spa/components/organisms/mypage/order/model/reorder-acceptability-state'
import OrderProduct from '@spa/components/organisms/mypage/order/OrderProduct.vue'
import ReorderNotification, { ReorderNotificationDep } from '@spa/components/organisms/mypage/order/reorder/ReorderNotification.vue'
import { selectOrderStateView } from '@spa/components/organisms/mypage/order/state/model/select-order-state-view'
import { AddToCartButtonDependency } from '@spa/components/pages/mypage/order/models/add-to-cart-button-dependency'
import { PluginMixin } from '@spa/mixins/plugin'
import {
  getAddress1,
  getAddress2,
  getDeliveryName,
  getSubTotalPrice,
} from '@spa/store/modules/orderHistory/functions/order'
import { Order, Product } from '@spa/store/modules/orderHistory/types'
import dateFormat from 'date-fns/format'
import { fromNullable } from 'fp-ts/lib/Option'
import _ from 'lodash'
import { VueConstructor } from 'vue'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'

@Component({
  components: {
    OrderProduct,
    CopyButton,
    ReorderNotification,
  },
})
export default class OrderDetail extends Mixins<PluginMixin>(PluginMixin) {
  @Prop({ required: true })
  readonly order: Order

  @Prop({ required: true })
  readonly pointIsEnabled: boolean

  @Prop({ required: true })
  readonly statusView: OrderStatusViewModel

  @Prop({ required: true })
  readonly reorderAcceptabilityState: ReorderAcceptabilityState

  @Prop({ required: true })
  readonly addToCartButtonDependency: AddToCartButtonDependency

  readonly behaviorConfig = new BehaviorConfig()

  get orderStateView(): VueConstructor {
    return selectOrderStateView(this.order, this.statusView)
  }

  get statusDescription(): string | undefined {
    return this.statusView.description.forDetailView
  }

  get reorderNotificationData(): ReorderNotificationDep | undefined {
    if (!reorderIsAcceptable(this.reorderAcceptabilityState)) {
      return undefined
    }

    return {
      title: this.reorderAcceptabilityState.view.title,
      descriptions: this.reorderAcceptabilityState.view.description,
      link: this.reorderAcceptabilityState.createReorderPath(this.order.id),
      buttonLabel: this.reorderAcceptabilityState.view.buttonLabel,
    }
  }

  get orderIdLabel(): string {
    return this.order.aux_string_3 || this.order.shortid
  }

  get deliveryUserName(): string {
    return getDeliveryName(this.order)
  }

  get zipcode(): string {
    return this.order.delivery.contact.address.code
  }

  get address1(): string {
    return getAddress1(this.order)
  }

  get address2(): string {
    return getAddress2(this.order)
  }

  get deliveryDate(): string {
    /**
     * new Dateは引数によって予測不可能な挙動をする。とくにnullとundefinedで挙動が変わる。
     * 一般にその挙動を吸収した関数や、日付型のラッパを定義してもよいが、どこで定義すべきかという問題もあり、また日付周りをサードパーティライブラリに任せることも含めて、
     * 強く必要性が出てきてから検討することにしたい。
     * @link https://github.com/my-color/front/issues/6601#issuecomment-1149454628
     */
    if (!this.order.delivery.date) {
      return ''
    }

    return dateFormat(new Date(this.order.delivery.date), 'YYYY/MM/DD')
  }

  get deliveryTimeSlot(): string {
    return this.order.delivery.timeSlotLabel
  }

  get subtotalPrice(): number {
    return getSubTotalPrice(this.order)
  }

  get totalPrice(): number {
    return this.order.chargeSheet.total
  }

  get deliveryPrice(): number {
    return this.order.isFreeShippingOnCatalogOnly ? 0 :_.get(this.order.chargeSheet.delivery, 'priceIncludingTax.price')
  }

  get chargePrice(): number {
    return _.get(this.order.chargeSheet.payment, 'priceNoTax.price')
  }

  get serviceFee(): number | undefined {
    return fromNullable(this.order.chargeSheet.serviceFee).fold(
      (void 0),
      fee => fee.priceIncludingTax.price
    )
  }

  get caratFormatted(): string {
    return this.rewardCarat ? Number(this.rewardCarat).toLocaleString('ja-JP') : '0'
  }

  get rewardCarat(): number {
    const caratRate = 0.01
    const subtotalWithoutTax = Math.ceil(this.subtotalPrice / (1 + MyColorConfig.TAX_RATE))

    return Math.floor(subtotalWithoutTax * caratRate)
  }

  get paymentMethod(): string {
    return getPaymentLabel(this.order.payment.method)
  }

  get estimatedPoints(): number {
    return this.order.estimatedPoints || 0
  }

  get discountPrice(): number {
    return this.order.discount ? this.order.discount.amount : 0
  }

  get usedPoint(): number {
    const point = this.order.payment.point

    return point ? point.amount : 0
  }

  get showDeliveryMethodLabel(): boolean {
    return this.order.delivery.deliveryMethodLabel !== undefined
  }
  get deliveryMethodLabel(): string {
    return getOrderHistoryDeliveryMethodLabel({
      purchaseDeliveryMethod: this.order.delivery.deliveryMethod,
      purchaseDeliveryMethodLabel: this.order.delivery.deliveryMethodLabel,
      shippingActualDeliveryMethodLabel: this.order.delivery.actualDeliveryMethodLabel,
      unattendedDeliveryLabel: this.order.delivery.unattendedDeliveryLabel || undefined,
    })
  }

  get trackingId(): string | undefined {
    // TODO: Use property when shipmentOrder is strictly typed.
    return _.get(this.order.shipmentOrder, 'external_trackingid')
  }

  get showTrackingId(): boolean {
    const trackingIdExist = this.trackingId !== undefined
    const catalogExist = this.catalogProducts.length !== 0

    return trackingIdExist && !catalogExist
  }

  get catalogProducts(): Product[] {
    // TODO OrderListItem にも重複した実装があるので共通化する
    return _.filter(this.orderProducts, product => product.isCatalogType)
  }

  get orderProducts(): Product[] {
    return _.filter(this.order.products, product => !product.isChild)
  }

  get sortedProducts(): Product[] {
    return _.sortBy(this.orderProducts, product => product.isNovelty)
  }

  get orderDateTime(): string {
    return dateFormat(new Date(this.order.createdAt), 'YYYY/MM/DD HH:mm')
  }

  formatPrice(price: number, defaultValue: string | null = null): string {
    if (!price) {
      return defaultValue || '￥0'
    }

    return price.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' })
  }

  get formatPoint(): (point: number) => string {
    return formatPoint
  }

  get editedOrderMessage(): string | null {
    return getEditedOrderMessage(this.order)
  }

  get addToCartButtonEligibilityChecker(): AddToCartButtonEligibilityChecker {
    return this.addToCartButtonDependency.eligibilityCheckerBuilder.buildForOrder({ order: this.order })
  }
}
